import { parseLocalDate, toUtcMidnightInstant } from '../../../../domain/utils/local-date';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../../domain/services/breadcrumb/breadcrumb.service';
import { HolidayService } from '../../../../domain/services/holidays/holiday.service';
import { HolidayModel } from '../../../../domain/models/holiday.model';

export const ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Last'];

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Day',
];

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

@Component({
  selector: 'app-holiday-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatRadioModule,
    MatSelectModule,
  ],
  templateUrl: './holiday-form.component.html',
  styleUrl: './holiday-form.component.css',
})
export class HolidayFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  holidayId: number | null = null;

  ordinals = ORDINALS;
  daysOfWeek = DAYS_OF_WEEK;
  months = MONTHS;

  get isSpecific(): boolean {
    return this.form?.get('type')?.value === 'specific';
  }

  get isConfigured(): boolean {
    return this.form?.get('type')?.value === 'configured';
  }

  get subtitle(): string {
    return this.isEditMode ? 'Edit holiday' : 'Create a new holiday';
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly holidayService: HolidayService
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/holidays', label: 'Administrative' },
      { icon: 'calendar_month', url: '/app/admin/holidays', label: 'Holidays' },
    ]);

    this.form = this.fb.group({
      type: ['specific', Validators.required],
      name: ['', Validators.required],
      // Specific date
      date: [null],
      // Configured date
      ordinal: ['First'],
      dayOfWeek: ['Monday'],
      month: ['January'],
    });

    const idParam = this.route.snapshot.params['id'];
    if (idParam && idParam !== 'new') {
      this.isEditMode = true;
      this.holidayId = Number(idParam);
      this.holidayService.getHoliday(this.holidayId).subscribe({
        next: (holiday) => {
          if (holiday) {
            this.form.patchValue({
              type: holiday.type,
              name: holiday.name,
              date: parseLocalDate(holiday.date as any),
              ordinal: holiday.ordinal ?? 'First',
              dayOfWeek: holiday.dayOfWeek ?? 'Monday',
              month: holiday.month ?? 'January',
            });
          }
        },
        // Backend GET /holiday/{id} previously ignored the id path variable (fixed
        // server-side); keep an explicit error path so a future regression fails loud
        // instead of silently leaving the form blank.
        error: () => console.error(`Failed to load holiday ${this.holidayId} for editing.`),
      });
    }
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.value;
    const payload: Omit<HolidayModel, 'id'> = {
      name: raw.name,
      type: raw.type,
      year: raw.date ? parseLocalDate(raw.date)!.getFullYear() : new Date().getFullYear(),
    };

    if (raw.type === 'specific') {
      // Instant at UTC midnight of the chosen day, so the day survives the round trip.
      payload.date = toUtcMidnightInstant(raw.date) as any;
    } else {
      payload.ordinal = raw.ordinal;
      payload.dayOfWeek = raw.dayOfWeek;
      payload.month = raw.month;
    }

    if (this.isEditMode && this.holidayId !== null) {
      this.holidayService.updateHoliday(this.holidayId, payload).subscribe(() => {
        this.router.navigate(['/app/admin/holidays']);
      });
    } else {
      this.holidayService.createHoliday(payload).subscribe(() => {
        this.router.navigate(['/app/admin/holidays']);
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/app/admin/holidays']);
  }
}
